import { useState, useEffect, useLayoutEffect, useRef } from 'react'
import { Button, Input } from '@you/ui'
import EmptyState from '@/components/common/EmptyState'
import { Icon } from '@/components/Icon'
import { useNavigate } from 'react-router-dom'
import AgentItem from '@/components/Agents/AgentItem'
import { AgentRes, AgentType } from '@/api/types/agent.type'
import { agentService } from '@/api/services/agent.service'
import { AGENT_CATEGORY_OPTIONS_WITH_ALL } from '@/constants/agent'
import emptyAgent from '@/assets/images/empty_agent.png'
import { useRecentAgentsStore } from '@/stores/recentAgentsStore'
import { useSearchAllAgents, useSearchMyAgents, useHomeDisplayAgents } from '@/api/hooks/useAgent'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import { PlatformPicksSection } from '@/components/Home'

type TabType = 'platform' | 'all' | 'my'
type CategoryType = 'all' | AgentType

// 智能体数据接口 - 使用 AgentRes 类型
interface AgentItem extends AgentRes {
  // 可以在这里添加额外的 UI 相关属性
  status: 'published' | 'unpublished'
}

const Agents = () => {
  const [activeTab, setActiveTab] = useState<TabType>('platform')
  const [activeCategory, setActiveCategory] = useState<CategoryType>('all')
  const [searchKeyword, setSearchKeyword] = useState('')
  const [agents, setAgents] = useState<AgentItem[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const navigate = useNavigate()
  const { addRecentAgent, removeRecentAgent } = useRecentAgentsStore()
  const { currentWorkspace } = useWorkspaceStore()

  // Slider positioning via refs
  const platformTabRef = useRef<HTMLButtonElement>(null)
  const allTabRef = useRef<HTMLButtonElement>(null)
  const myTabRef = useRef<HTMLButtonElement>(null)
  const SLIDER_WIDTH = 57
  const [sliderLeft, setSliderLeft] = useState(0)

  useLayoutEffect(() => {
    const refMap: Record<TabType, React.RefObject<HTMLButtonElement | null>> = {
      platform: platformTabRef,
      all: allTabRef,
      my: myTabRef,
    }
    const btn = refMap[activeTab].current
    if (btn) {
      setSliderLeft(btn.offsetLeft + (btn.offsetWidth - SLIDER_WIDTH) / 2)
    }
  }, [activeTab])

  // Platform picks
  const { data: homeDisplayData, isLoading: platformLoading } = useHomeDisplayAgents(
    currentWorkspace?.id?.toString()
  )
  const platformPicks = homeDisplayData?.platformPicks ?? []

  // 防抖搜索关键词
  const [debouncedSearchKeyword, setDebouncedSearchKeyword] =
    useState<string>('')
  const searchTimeoutRef = useRef<number | undefined>(undefined)

  // 根据 activeTab 选择对应的 hook
  const {
    data: allAgents,
    error: allAgentsError,
    isLoading: allAgentsLoading,
    mutate: mutateAllAgents,
  } = useSearchAllAgents({
    keyWord: debouncedSearchKeyword,
    workspaceId: currentWorkspace?.id?.toString(),
  })

  const {
    data: myAgents,
    error: myAgentsError,
    isLoading: myAgentsLoading,
    mutate: mutateMyAgents,
  } = useSearchMyAgents({
    keyWord: debouncedSearchKeyword,
    workspaceId: currentWorkspace?.id?.toString(),
  })

  // 根据当前 tab 选择数据
  const currentAgents = activeTab === 'all' ? allAgents : myAgents
  const currentError = activeTab === 'all' ? allAgentsError : myAgentsError
  const currentLoading =
    activeTab === 'all' ? allAgentsLoading : myAgentsLoading

  console.log('currentAgents', currentAgents)

  // 防抖效果
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    searchTimeoutRef.current = window.setTimeout(() => {
      setDebouncedSearchKeyword(searchKeyword)
    }, 500)

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [searchKeyword])

  // 监听 hook 数据变化，更新本地状态
  useEffect(() => {
    if (currentAgents) {
      // 转换数据格式，添加 UI 相关属性
      const transformedAgents: AgentItem[] = currentAgents.map((agent) => ({
        ...agent,
        status: agent.isPublish === 1 ? 'published' : 'unpublished',
      }))
      setAgents(transformedAgents)
    }
  }, [currentAgents])

  // 监听错误状态
  useEffect(() => {
    if (currentError) {
      setError('获取智能体列表失败，请稍后重试')
    } else {
      setError(null)
    }
  }, [currentError])

  // 监听加载状态
  useEffect(() => {
    setLoading(currentLoading || false)
  }, [currentLoading])

  // 分类选项 - 使用常量
  const categoryOptions = AGENT_CATEGORY_OPTIONS_WITH_ALL

  // 过滤智能体数据 - 更新过滤逻辑
  const filteredAgents = agents.filter((agent) => {
    const matchesCategory =
      activeCategory === 'all' || activeCategory === agent.type

    return matchesCategory
  })

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab)
  }

  const handleCategoryChange = (category: CategoryType) => {
    setActiveCategory(category)
  }

  const handleSearch = (value: string) => {
    setSearchKeyword(value)
  }

  const handleCreate = () => {
    navigate('/agents/add')
  }

  // 处理编辑智能体
  const handleEditAgent = (agent: AgentRes) => {
    console.log('编辑智能体:', agent)
    // 跳转到编辑页面，使用 agentId 作为参数
    if (agent.agentId) {
      navigate(`/agents/edit/${agent.agentId}`)
    } else {
      console.error('智能体ID不存在，无法编辑')
      // TODO: 显示错误提示
    }
  }

  // 处理删除智能体
  const handleDeleteAgent = async (agentId: string | number) => {
    try {
      await agentService.deleteAgent(String(agentId))
      console.log('智能体删除成功:', agentId)
      // 从近期使用列表中移除该智能体
      removeRecentAgent(String(agentId))
      // 删除成功后重新获取列表
      if (activeTab === 'all') {
        await mutateAllAgents()
      } else {
        await mutateMyAgents()
      }
    } catch (error) {
      console.error('删除智能体失败:', error)
      // TODO: 显示错误提示
    }
  }

  const handleAgentClick = (agent: AgentRes) => {
    addRecentAgent(agent)
    navigate(`/agents/chat/${agent.id}`)
  }

  return (
    <div className="min-w-[800px] max-w-[1200px] mx-auto h-full">
      {/* 头部区域 */}
      <div className="my-4 border-b border-[#D9D9D9] pb-[10px]">
        {/* 标题和切换 */}
        <div className="flex items-center justify-between">
          <div className="relative">
            <div className="flex items-center space-x-8">
              <button
                ref={platformTabRef}
                className={`text-[24px] font-[400] transition-all duration-300 h-[36px] relative ${
                  activeTab === 'platform'
                    ? 'text-[#7660E0] font-[900] text-[26px]'
                    : 'text-[#333333]'
                }`}
                onClick={() => handleTabChange('platform')}
              >
                <Icon
                  name="star"
                  className={`w-4 h-4 absolute top-[-8px] right-[-16px] ${
                    activeTab === 'platform' ? 'opacity-100' : 'opacity-0'
                  }`}
                />
                平台精选
              </button>
              <button
                ref={allTabRef}
                className={`text-[24px] font-[400] transition-all duration-300 h-[36px] relative ${
                  activeTab === 'all'
                    ? 'text-[#7660E0] font-[900] text-[26px]'
                    : 'text-[#333333]'
                }`}
                onClick={() => handleTabChange('all')}
              >
                <Icon
                  name="star"
                  className={`w-4 h-4 absolute top-[-8px] right-[-16px] ${
                    activeTab === 'all' ? 'opacity-100' : 'opacity-0'
                  }`}
                />
                所有智能体
              </button>
              <button
                ref={myTabRef}
                className={`text-[24px] font-[400] transition-all duration-300 h-[36px] ${
                  activeTab === 'my'
                    ? 'text-[#7660E0] font-[900] text-[26px]'
                    : 'text-[#333333]'
                }`}
                onClick={() => handleTabChange('my')}
              >
                <Icon
                  name="star"
                  className={`w-4 h-4 absolute top-[-8px] right-[-16px] ${
                    activeTab === 'my' ? 'opacity-100' : 'opacity-0'
                  }`}
                />
                我的智能体
              </button>
            </div>

            {/* 紫色滑块 */}
            <div
              className="absolute bottom-[-14px] h-[6px] w-[57px] rounded-[7px] transition-all duration-300 ease-out"
              style={{
                background:
                  'linear-gradient(307.54deg, #8752FF 13.46%, #A37BFF 73.7%)',
                transform: `translateX(${sliderLeft}px)`,
              }}
            />
          </div>

          {/* 搜索栏和新增按钮 */}
          <div className="flex items-center space-x-4">
            <div className="relative">
              <Icon
                name="search"
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none z-10"
              />
              <Input
                type="text"
                placeholder="搜索智能体..."
                value={searchKeyword}
                onChange={(e) => handleSearch(e.target.value)}
                className="px-0 w-[300px] pl-9 pr-2 rounded-full bg-[#ECE3FF8A] border-none"
              />
            </div>
            <Button
              onClick={handleCreate}
              className="gradient-primary rounded-full w-[188px] gap-2"
            >
              <Icon name="star" className="w-4 h-4" />
              <span>新建智能体</span>
            </Button>
          </div>
        </div>
      </div>

      {/* 分类选项卡 - 平台精选下不显示 */}
      {activeTab !== 'platform' && (
        <div className="mb-6">
          <div className="flex items-center space-x-2 p-1">
            {categoryOptions.map((category) => (
              <button
                key={category.value}
                onClick={() =>
                  handleCategoryChange(category.value as CategoryType)
                }
                className={`px-4 py-2 rounded-full text-[16px] transition-all duration-200 ${
                  activeCategory === category.value
                    ? 'text-[#3C19E9] bg-[#F3F2FF]'
                    : 'text-[#666666] bg-white'
                }`}
                style={{ height: '39px' }}
              >
                {category.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* 内容区域 */}
      <div className="flex-1">
        {activeTab === 'platform' ? (
          platformLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="flex items-center space-x-2 text-gray-600">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                <span>加载中...</span>
              </div>
            </div>
          ) : (
            <PlatformPicksSection
              title=""
              agents={platformPicks}
              onAgentClick={handleAgentClick}
            />
          )
        ) : loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="flex items-center space-x-2 text-gray-600">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              <span>加载中...</span>
            </div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center text-gray-600">
              <div className="text-2xl mb-3">⚠️</div>
              <div className="mb-2">{error}</div>
              <Button
                onClick={() => {
                  if (activeTab === 'all') {
                    mutateAllAgents()
                  } else {
                    mutateMyAgents()
                  }
                }}
                className="bg-gradient-primary mt-2"
              >
                重试
              </Button>
            </div>
          </div>
        ) : filteredAgents.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredAgents.map((agent) => (
              <AgentItem
                key={agent.id}
                {...agent}
                onClick={handleAgentClick}
                onEdit={handleEditAgent}
                onDelete={handleDeleteAgent}
              />
            ))}
          </div>
        ) : (
          <EmptyState
            className="w-[380px] h-[380px]"
            image={emptyAgent}
            description="暂无智能体数据，点击"
            actionText="新建智能体"
            offText="创建一个试试吧"
            onAction={handleCreate}
          />
        )}
      </div>
    </div>
  )
}

export default Agents
